import { z } from "zod";
import { getDomainByName } from "@/constants/domains";
import { makeEntryCode } from "@/utils/codes";
import { parseSubdomainName } from "@/utils/subdomain";
import type {
  ScaleDomainNode,
  ScaleEntry,
  ScaleGoalNode,
  ScaleLibrary,
  ScaleGoalLevel
} from "@/types/scale";

interface RawScaleGoal {
  code: string;
  title: string;
  standard: string;
  examples?: string;
  activities: string[];
  short_term_goals?: RawScaleGoal[];
}

const goalSchema: z.ZodType<RawScaleGoal> = z.lazy(() =>
  z
    .object({
      code: z.string().min(1),
      title: z.string().min(1),
      standard: z.string().trim(),
      examples: z.string().trim().default(""),
      activities: z.array(z.string().trim()),
      short_term_goals: z.array(goalSchema).default([])
    })
    .strict(),
);

const subdomainSchema = z
  .object({
    name: z.string().min(1),
    long_term_goals: z.array(goalSchema)
  })
  .strict();

const domainSchema = z
  .object({
    name: z.string().min(1),
    subdomains: z.array(subdomainSchema)
  })
  .strict();

const scaleSchema = z
  .object({
    domains: z.array(domainSchema)
  })
  .strict();

export type RawScaleData = z.infer<typeof scaleSchema>;

interface GoalContext {
  domainName: string;
  domainCode: ReturnType<typeof getDomainByName>["code"];
  subdomainName: string;
  subdomainCode: string;
  subdomainLabel: string;
}

interface EntryConstructionInput {
  goal: RawScaleGoal;
  level: ScaleGoalLevel;
  context: GoalContext;
  parentCode?: string;
  longTermDetail: {
    code: string;
    title: string;
    standard: string;
  };
}

export function parseScaleLibrary(raw: unknown): ScaleLibrary {
  const data = scaleSchema.parse(raw);

  const entries: ScaleEntry[] = [];
  const entryIndex = new Map<string, ScaleEntry>();

  const domains: ScaleDomainNode[] = data.domains.map((domain) => {
    const domainMeta = getDomainByName(domain.name);
    const subdomains = domain.subdomains.map((subdomain) => {
      const parsedSubdomain = parseSubdomainName(subdomain.name);
      const uniqueGoals = deduplicateGoalList(
        subdomain.long_term_goals,
        `${domainMeta.code}-${parsedSubdomain.code}`,
      );
      const goalNodes = uniqueGoals
        .map((goal) =>
          buildGoalTree(goal, {
            domainName: domainMeta.name,
            domainCode: domainMeta.code,
            subdomainName: subdomain.name,
            subdomainCode: parsedSubdomain.code,
            subdomainLabel: parsedSubdomain.label || subdomain.name
          }),
        )
        .filter((node): node is ScaleGoalNode => Boolean(node));

      return {
        subdomainCode: parsedSubdomain.code,
        subdomainName: subdomain.name,
        subdomainLabel: parsedSubdomain.label,
        longTermGoals: goalNodes
      };
    });

    return {
      domainCode: domainMeta.code,
      domainName: domainMeta.name,
      subdomains
    };
  });

  function buildGoalTree(
    goal: RawScaleGoal,
    context: GoalContext,
    longTermDetail?: EntryConstructionInput["longTermDetail"],
    parentCode?: string,
  ): ScaleGoalNode | null {
    const isLongTerm = !longTermDetail;
    const level: ScaleGoalLevel = isLongTerm ? "long_term" : "short_term";
    const detail = longTermDetail ?? {
      code: goal.code,
      title: goal.title,
      standard: goal.standard
    };

    const entry = constructEntry({
      goal,
      level,
      context,
      parentCode,
      longTermDetail: detail
    });

    if (entryIndex.has(entry.code)) {
      console.warn(`[scale-parser] duplicate entry code detected: ${entry.code}, skipping`);
      return null;
    }

    entries.push(entry);
    entryIndex.set(entry.code, entry);

    const children = (goal.short_term_goals ?? [])
      .map((child: RawScaleGoal) => buildGoalTree(child, context, detail, entry.code))
      .filter((childNode): childNode is ScaleGoalNode => Boolean(childNode));

    return {
      code: entry.code,
      title: entry.shortTermGoal?.title ?? entry.longTermGoal.title,
      standard: entry.standard,
      examples: entry.examples,
      activities: entry.activities,
      level,
      children
    };
  }

  function constructEntry(input: EntryConstructionInput): ScaleEntry {
    const { goal, level, context, parentCode, longTermDetail } = input;
    const code = makeEntryCode(context.domainCode, context.subdomainCode, goal.code);
    return {
      code,
      level,
      domainCode: context.domainCode,
      domainName: context.domainName,
      subdomainCode: context.subdomainCode,
      subdomainName: context.subdomainName,
      subdomainLabel: context.subdomainLabel,
      longTermGoal: { ...longTermDetail },
      shortTermGoal:
        level === "short_term"
          ? {
              code: goal.code,
              title: goal.title,
              standard: goal.standard
            }
          : undefined,
      parentCode,
      activities: goal.activities,
      examples: goal.examples ?? "",
      standard: goal.standard
    };
  }

  return {
    entries,
    domains,
    entryIndex
  };
}

function deduplicateGoalList(goals: RawScaleGoal[], context: string): RawScaleGoal[] {
  const seen = new Set<string>();
  const uniqueGoals: RawScaleGoal[] = [];

  for (const goal of goals) {
    if (seen.has(goal.code)) {
      console.warn(`[scale-parser] duplicate goal code ${goal.code} detected in ${context}, skipping`);
      continue;
    }
    seen.add(goal.code);
    uniqueGoals.push({
      ...goal,
      short_term_goals: deduplicateGoalList(goal.short_term_goals ?? [], `${context}.${goal.code}`)
    });
  }

  return uniqueGoals;
}
